import * as React from "react";
import { useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Cookies from "js-cookie";
import { CommonButton } from "@/components/common/buttons/CommonButton";
import { Button } from "@/components/molecules/Button";
import { EmptyResultMessage } from "@/components/atoms/EmptyResultMessage";
import Title from "@/components/molecules/Title";
import { AutoCompleteMultiple } from "@/components/molecules/AutoCompleteMultiple";
import {
  AccidentReportsConstants,
  DEFAULT_SEARCH_CONDITION,
  PAGE_SIZE,
  SavedState,
} from "./AccidentReportsConstants";
import { Office, ONLY_CONTRACT } from "@/models/office";
import { getOfficesAPI } from "@/util/api/API";
import {
  GetAccidentReportFormUrl,
  MULTI_OFFICE_ID,
} from "@/common/CommonConstants";
import { getOfficeOptionLabel } from "@/util";
import {
  AccidentReport,
  SearchAccidentReportsCondition,
} from "@/models/accidentReport";
import {
  countAccidentReportsAPI,
  getAccidentReportsAPI,
} from "@/util/api/accidentReports";
import { AccidentReportsList } from "./AccidentReportsList";
import { Typography } from "@mui/material";
import { ResponsibleRegistrationPrompt } from "@/components/accidentReportPersons/ResponsibleRegistrationPrompt";

export const AccidentReportsPage = () => {
  const [searchCondition, setSearchCondition] =
    React.useState<SearchAccidentReportsCondition>(DEFAULT_SEARCH_CONDITION);

  const [total, setTotal] = React.useState<number>(0);
  const [page, setPage] = React.useState<number>(0);
  const [reports, setReports] = React.useState<AccidentReport[]>([]);

  const [offices, setOffices] = React.useState<Office[]>([]);
  const [officesSelected, setOfficesSelected] = React.useState<Office[]>([]);

  const [noSearchResult, setNoSearchResult] = React.useState<boolean>(false);
  const [initSearchFlag, setInitSearchFlag] = React.useState<boolean>(false);
  const { state }: { state?: SavedState } = useLocation();

  // 事故報告を取得・更新する
  const updateReports = React.useCallback(
    (condition: SearchAccidentReportsCondition, page: number) => {
      getAccidentReportsAPI(condition, page - 1, PAGE_SIZE).then((res) => {
        setReports(res.data);
        setPage(page);
      });

      countAccidentReportsAPI(condition).then((res) => {
        const count = res.data?.count ?? 0;
        setTotal(count);
        setNoSearchResult(count === 0);
      });
    },
    [],
  );

  const handleChangeOffices = React.useCallback(
    (offices: Office[]) => {
      setOfficesSelected(offices);
      setSearchCondition({
        ...searchCondition,
        officeIds: offices?.map(({ id }) => id) ?? [],
      });
      Cookies.set(MULTI_OFFICE_ID, offices.map(({ id }) => id).join(","));
    },
    [searchCondition],
  );

  const handleOnPageChange = React.useCallback(
    (page: number) => {
      updateReports(searchCondition, page);
    },
    [searchCondition, updateReports],
  );

  // 検索条件に基づいて事故報告一覧をフィルタリングする
  const handleOnClickSearch = React.useCallback(() => {
    updateReports(searchCondition, 1);
  }, [searchCondition, updateReports]);

  // 検索条件をクリアする
  const handleOnClickReset = React.useCallback(() => {
    setOfficesSelected([]);
    setSearchCondition(DEFAULT_SEARCH_CONDITION);
  }, []);

  // 事故報告フォームを別タブで開く
  const handleOpenAccidentForm = React.useCallback(() => {
    window.open(GetAccidentReportFormUrl(), "_blank", "noopener,noreferrer");
  }, []);

  const getOffices = React.useCallback(async () => {
    try {
      const res = await getOfficesAPI(ONLY_CONTRACT);
      setOffices(res.data || []);
      const founds = res.data.filter((d) =>
        Cookies.get(MULTI_OFFICE_ID)?.split(",").includes(d.id.toString()),
      );
      if (founds.length) {
        setOfficesSelected(founds);
        setSearchCondition({
          ...searchCondition,
          officeIds: founds.map(({ id }) => id),
        });
      }

      setInitSearchFlag(true);
    } catch (err) {
      console.log("==Error when get offices==", err);
    }
  }, [searchCondition]);

  React.useEffect(() => {
    getOffices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!initSearchFlag) {
      return;
    }

    // 保存された検索条件があれば、その検索条件をもとに事故報告一覧を取得する
    if (state != null && state.officesSelected != null) {
      setOfficesSelected(state.officesSelected);
      setPage(state.page);
      setSearchCondition(state.searchCondition);
      updateReports(state.searchCondition, state.page);
    } else {
      handleOnClickSearch();
    }

    setInitSearchFlag(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initSearchFlag, state]);

  return (
    <React.Fragment>
      <Grid
        container
        spacing={2}
        sx={{ paddingLeft: 2, paddingRight: 2, mt: 3 }}
      >
        <Grid
          container
          spacing={2}
          sx={{ display: "flex", pl: 2, mb: 2, alignItems: "center" }}
        >
          <Title style={{ flexGrow: 1 }}>
            {AccidentReportsConstants.AccidentReports}
          </Title>
          <Button
            sx={{ backgroundColor: "#e57373" }}
            onClick={handleOpenAccidentForm}
            label={AccidentReportsConstants.OpenAccidentForm}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ paddingLeft: 2, paddingRight: 2 }}>
        <Grid container spacing={2}>
          {/* 登録中の事故報告責任者が0〜1名の営業所がある場合の登録促進バナー（事故報告責任者一覧と同一内容） */}
          <ResponsibleRegistrationPrompt />
          <Grid item xs={12} md={12} lg={12}>
            <Paper
              sx={{
                p: 2,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid container spacing={2} justifyContent="space-between">
                <Grid item xs={6}>
                  <AutoCompleteMultiple
                    options={offices}
                    limit={100}
                    forSearch
                    getOptionLabel={getOfficeOptionLabel}
                    value={officesSelected}
                    onChange={handleChangeOffices}
                    label={AccidentReportsConstants.CompanyOffice}
                  />
                </Grid>
                <Grid item xs={12} xl={2.7} container justifyContent="flex-end">
                  <CommonButton
                    btnType="Search"
                    onClickBtn={handleOnClickSearch}
                  />
                  <CommonButton
                    sx={{ ml: 2 }}
                    btnType="ResetSearchCriteria"
                    onClickBtn={handleOnClickReset}
                  />
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          {!noSearchResult && (
            <Grid container direction="column">
              <Typography variant="body2" sx={{ mt: 1, ml: 2 }}>
                ※ご認識のない事故報告がある場合は貴社内でご確認、もしくは弊社までご報告ください
              </Typography>
            </Grid>
          )}
          <Grid item xs={10} md={10} lg={14}>
            <Paper sx={{ p: 1, display: "flex", flexDirection: "column" }}>
              <AccidentReportsList
                onChangePage={handleOnPageChange}
                reports={reports}
                pagingData={{ page, total, size: PAGE_SIZE }}
              />
              {noSearchResult && <EmptyResultMessage />}
            </Paper>
          </Grid>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};
